import { Searchbar } from '@/components/Searchbar';
import { GeoStreetRef } from '@/models/audiences';
import { GeoStreet } from '@/services/endpoints/geo';
import { FC, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { GEO_MAX_STREETS } from '../../expression';
import { CheckSquare } from '../RecipientsLists';

export const streetKey = (ref: GeoStreetRef) => `${ref.city}|${ref.street}`;

type StreetPickerProps = {
  streets: GeoStreet[] | undefined;
  isLoading: boolean;
  selected: GeoStreetRef[];
  onChange: (selected: GeoStreetRef[]) => void;
};

/** Searchable checkbox list of the streets of the selected lists, sorted by citizens as served by the API. */
export const StreetPicker: FC<StreetPickerProps> = ({ streets, isLoading, selected, onChange }) => {
  const { t } = useTranslation('comuni');
  const [query, setQuery] = useState('');

  const selectedKeys = useMemo(() => new Set(selected.map(streetKey)), [selected]);
  const multiCity = useMemo(() => new Set((streets ?? []).map((s) => s.city)).size > 1, [streets]);
  const visible = useMemo(() => {
    const q = query.trim().toLowerCase();
    return (streets ?? []).filter((s) => !q || s.street.toLowerCase().includes(q) || s.city.toLowerCase().includes(q));
  }, [streets, query]);

  const toggle = (street: GeoStreet) => {
    const ref = { city: street.city, street: street.street };
    if (selectedKeys.has(streetKey(ref))) return onChange(selected.filter((s) => streetKey(s) !== streetKey(ref)));
    if (selected.length >= GEO_MAX_STREETS) return undefined;
    return onChange([...selected, ref]);
  };

  return (
    <div className='grid gap-3'>
      <Searchbar
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        placeholder={t('composer.geo.searchStreet')}
        name='search-streets'
        labelClassName='max-w-none'
      />
      <div className='max-h-[300px] overflow-y-auto rounded-xl border bg-white-100'>
        {isLoading && <p className='px-4 py-3 text-[13.5px] text-quaternary-400'>{t('composer.geo.loading')}</p>}
        {!isLoading && visible.length === 0 && <p className='px-4 py-3 text-[13.5px] text-quaternary-400'>{t('composer.geo.noStreets')}</p>}
        {visible.map((street) => {
          const checked = selectedKeys.has(streetKey(street));
          return (
            <button
              key={streetKey(street)}
              type='button'
              aria-pressed={checked}
              onClick={() => toggle(street)}
              className={`flex w-full items-center gap-3 border-b px-4 py-2.5 text-left last:border-b-0 hover:bg-secondary-background ${checked ? 'bg-brand-50' : ''}`}
            >
              <CheckSquare checked={checked} />
              <span className='min-w-0 flex-1 truncate text-[13.5px] font-semibold capitalize text-primary-900'>
                {street.street.toLowerCase()}
                {multiCity && <span className='ml-1.5 font-normal text-quaternary-400'>· {street.city.toLowerCase()}</span>}
              </span>
              <span className='flex-none text-[12.5px] tabular-nums text-tertiary-600'>{street.citizens}</span>
            </button>
          );
        })}
      </div>
      {selected.length > 0 && (
        <div className='flex flex-wrap gap-2'>
          {selected.map((ref) => (
            <span
              key={streetKey(ref)}
              className='flex items-center gap-1.5 rounded-full border border-brand-200 bg-brand-50 px-3 py-1 text-[12.5px] font-bold capitalize text-brand-700'
            >
              {ref.street.toLowerCase()}
              <button
                type='button'
                onClick={() => onChange(selected.filter((s) => streetKey(s) !== streetKey(ref)))}
                className='text-brand-700 hover:text-red-600'
                aria-label={t('composer.removeFilter')}
              >
                ✕
              </button>
            </span>
          ))}
        </div>
      )}
      {selected.length >= GEO_MAX_STREETS && <p className='text-[12.5px] text-yellow-700'>{t('composer.geo.maxStreets', { max: GEO_MAX_STREETS })}</p>}
    </div>
  );
};
